import * as Sentry from '@sentry/react'

export interface SentryConfig {
  dsn: string
  environment: string
  tracesSampleRate: number
  replaysSessionSampleRate: number
  replaysOnErrorSampleRate: number
}

const getSentryConfig = (): SentryConfig | null => {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) {
    console.warn('Sentry DSN not configured. Error monitoring is disabled.')
    return null
  }

  return {
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
    replaysSessionSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1'),
    replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'),
  }
}

export const initializeSentry = (): void => {
  const config = getSentryConfig()

  if (!config) {
    return
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: config.tracesSampleRate,
    tracePropagationTargets: ['localhost', /^https:\/\/api\.memberfy\.(dev|net)/],
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
    beforeSend(event, hint) {
      const error = hint.originalException

      if (error && typeof error === 'object' && 'message' in error) {
        const errorMessage = String(error.message).toLowerCase()

        const ignoredErrors = [
          'resizeobserver loop',
          'non-error promise rejection',
          'network error',
          'loading chunk',
          'dynamically imported module',
        ]

        if (ignoredErrors.some(ignored => errorMessage.includes(ignored))) {
          return null
        }
      }

      return event
    },
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'Network request failed',
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      'cancelled',
    ],
  })

  attachReplay()
}

/**
 * Session replay is the heaviest part of the SDK and nothing needs it before the
 * page is up, so it is fetched (from Sentry's CDN) once the browser is idle and
 * attached to the running client. A failed fetch only means no replay.
 */
const attachReplay = (): void => {
  const load = () => {
    Sentry.lazyLoadIntegration('replayIntegration')
      .then((replayIntegration) => {
        Sentry.addIntegration(replayIntegration({ maskAllText: true, blockAllMedia: true }))
      })
      .catch(() => {})
  }
  if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(load, { timeout: 5000 })
  else setTimeout(load, 3000)
}

export const setSentryUser = (user: {
  id: string
  email?: string
  username?: string
  role?: string
}): void => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  })

  if (user.role) {
    Sentry.setTag('user.role', user.role)
  }
}

export const setSentryCommunity = (community: {
  id: string
  name?: string
  domain?: string
}): void => {
  Sentry.setContext('community', {
    id: community.id,
    name: community.name,
    domain: community.domain,
  })

  Sentry.setTag('community.id', community.id)

  if (community.domain) {
    Sentry.setTag('community.domain', community.domain)
  }
}

export const clearSentryUser = (): void => {
  Sentry.setUser(null)
}

export const captureException = (error: Error, context?: Record<string, any>): void => {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value)
      })
      Sentry.captureException(error)
    })
  } else {
    Sentry.captureException(error)
  }
}

export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info'): void => {
  Sentry.captureMessage(message, level)
}

export const addBreadcrumb = (breadcrumb: {
  message: string
  category?: string
  level?: Sentry.SeverityLevel
  data?: Record<string, any>
}): void => {
  Sentry.addBreadcrumb({
    message: breadcrumb.message,
    category: breadcrumb.category || 'custom',
    level: breadcrumb.level || 'info',
    data: breadcrumb.data,
  })
}

export { Sentry }
